/**
 * MA-UI-108: ZIP Code Validation Utilities
 * 
 * Client-side validation for ZIP code inputs in funnel runtime.
 * Mirrors backend validation logic for consistent UX.
 */

// US ZIP patterns
const ZIP_5_PATTERN = /^\d{5}$/;
const ZIP_PLUS_4_PATTERN = /^(\d{5})-?(\d{4})$/;

/**
 * Validate a ZIP code value
 * @param {string} value - The raw ZIP code input
 * @returns {{ isValid: boolean, normalized: string|null, error: string|null }}
 */
export function validateZipCode(value) {
  if (!value) {
    return { isValid: false, normalized: null, error: 'ZIP code is required' };
  }

  const cleaned = value.trim();

  if (!cleaned) {
    return { isValid: false, normalized: null, error: 'ZIP code cannot be empty' };
  }

  // Check 5-digit ZIP
  if (ZIP_5_PATTERN.test(cleaned)) {
    return { isValid: true, normalized: cleaned, error: null };
  }

  // Check ZIP+4 format
  const match = cleaned.match(ZIP_PLUS_4_PATTERN);
  if (match) {
    // Return the 5-digit portion for enrichment
    return { isValid: true, normalized: match[1], error: null };
  }

  // Invalid format
  return {
    isValid: false,
    normalized: null,
    error: 'Enter a valid 5-digit ZIP code'
  };
}

/**
 * Normalize a ZIP code to its 5-digit form
 * @param {string} value - The raw ZIP code input
 * @returns {string|null} The normalized 5-digit ZIP, or null if invalid
 */
export function normalizeZipCode(value) {
  const result = validateZipCode(value);
  return result.isValid ? result.normalized : null;
}

/**
 * Check if a value looks like it could be a ZIP (for format hints)
 * @param {string} value - The input value
 * @returns {boolean} True if partial ZIP pattern
 */
export function isPartialZip(value) {
  if (!value) return false;
  const cleaned = value.trim();
  // Allow partial entry: 1-5 digits, or 5 digits + hyphen + 0-4 digits
  return /^\d{1,5}$/.test(cleaned) || /^\d{5}-?\d{0,4}$/.test(cleaned);
}

/**
 * Format ZIP code as user types (add hyphen for ZIP+4)
 * @param {string} value - The input value
 * @returns {string} Formatted value
 */
export function formatZipInput(value) {
  if (!value) return '';
  // Remove non-digits except hyphen
  let cleaned = value.replace(/[^\d-]/g, '');
  // Remove extra hyphens
  cleaned = cleaned.replace(/-+/g, '-');
  // Limit length
  if (cleaned.replace('-', '').length > 9) {
    cleaned = cleaned.slice(0, 10);
  }
  return cleaned;
}
